import React, { useEffect, useState } from 'react';
import { useTagAndSegment } from '../../contexts/tagAndSegment';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import SegmentBuilderConditions from '../SegmentBuilderConditions';
import service from '../../service';
import { useAuth } from '../../contexts/auth';
import Modal from '../Modal';
import SegmentReview from '../SegmentReview';
import { useModal } from '../../contexts/modal';
import { makeBlankCondition } from '../../utils';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import BreadcrumbWrapper from '../BreadcrumbWrapper/index';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import ShowFor from '@Components/ShowFor';

const SegmentBuilder = ({ mode = 'create' }) => {
  const { openModal } = useModal();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    readAllTags,
    segmentConditions,
    setSegmentConditions,
    setSegmentName,
  } = useTagAndSegment();
  const { user } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      name: '',
    },
  });

  useEffect(() => {
    readAllTags();

    // Load segment data if in edit mode
    if (mode === 'edit' && id) {
      const loadSegment = async () => {
        try {
          const segment = await service.readSegmentById(id);
          setValue('name', segment.name);
          setSegmentName(segment.name);
          setSegmentConditions(segment.conditions);
        } catch (error) {
          console.error(error);
          toastError('Failed to load segment');
          navigate('/segments');
        }
      };
      loadSegment();
    }
  }, [mode, id, setValue, setSegmentName, setSegmentConditions, navigate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setSegmentConditions(makeBlankCondition());
      setSegmentName('');
    };
  }, [setSegmentConditions, setSegmentName]);

  const handleBack = () => {
    navigate('/segments');
  };

  // Update context when name changes
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'name') {
        setSegmentName(value.name || '');
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setSegmentName]);

  const areAllConditionsValid = () => {
    const checkNode = (node) => {
      if (!node) return false;

      if (!node.type) {
        return !!node.value;
      }

      if (node.conditions) {
        return node.conditions.every(checkNode);
      }

      return false;
    };

    return checkNode(segmentConditions);
  };

  const isFormValid = () => {
    const name = watch('name');
    return name && name.length > 0 && areAllConditionsValid();
  };

  const onSubmit = async (data) => {
    if (!areAllConditionsValid()) {
      return;
    }

    try {
      if (mode === 'edit') {
        await service.updateSegment(id, {
          name: data.name,
          conditions: segmentConditions,
        });
        toastSuccess('Successfully updated segment!');
      } else {
        await service.createSegment({
          name: data.name,
          createdBy: user.id,
          conditions: segmentConditions,
        });
        toastSuccess('Successfully created segment!');
      }
      navigate('/segments');
      setSegmentConditions(makeBlankCondition());
      setSegmentName('');
    } catch (error) {
      console.error(error);
      toastError(`${error}`);
    }
  };

  return (
    <>
      <BreadcrumbWrapper
        crumbArray={[
          { path: '/segments', label: 'Segments' },
          { path: `/segments/${id}`, label: 'Segment Details' },
        ]}
      />
      <div className="p-4">
        <Button
          onClick={handleBack}
          className="mb-4 rounded bg-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-400"
        >
          &larr; Back to Segments
        </Button>

        <div className="flex items-center justify-between">
          <h1 className="mb-4 text-2xl font-bold">
            {mode === 'edit' ? 'Edit Segment' : 'Create Segment'}
          </h1>
          <div className="flex items-center gap-2">
            <Button
              onClick={openModal}
              disabled={!isFormValid()}
              className={`rounded bg-gray-300 px-4 py-2 ${isFormValid()
                ? 'bg-gray-300 text-black'
                : 'cursor-not-allowed bg-gray-300 text-gray-500'
                }`}
            >
              Review segment
            </Button>

            <ShowFor permissions={['SEGMENTS_WRITE']}>
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={!isFormValid()}
                className={`rounded px-4 py-2 ${!isFormValid()
                  && 'cursor-not-allowed bg-gray-300 text-gray-500'
                  }`}
              >
                {mode === 'edit' ? 'Update segment' : 'Save segment'}
              </Button>
            </ShowFor>
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="segmentName" className="text-md">
            Name
          </label>
          <input
            {...register('name', {
              required: 'Segment name is required',
              minLength: { value: 1, message: 'Name cannot be empty' },
            })}
            type="text"
            id="segmentName"
            className={`w-full rounded-md border px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:outline-none ${errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="Enter segment name"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
          )}
        </div>

        <SegmentBuilderConditions />
      </div>
      <Modal className={'max-w-[80%]'}>
        <SegmentReview />
      </Modal>
    </>
  );
};

export default SegmentBuilder;
